import type { ResumeData, ResumeSettings } from '../types/resume';
import { mergeSettings, formatDate, pagePadding } from '../lib/resume-settings';

interface Props {
  data: ResumeData;
  settings?: ResumeSettings;
}

function renderDescription(description: string, lineSpacing: number, bScale: number) {
  if (!description) return null;
  const lines = description.split('\n').map((line) => line.trim()).filter(Boolean);
  const style = { fontSize: `${0.75 * bScale}rem`, lineHeight: lineSpacing };
  if (lines.length <= 1) {
    return <p className="mt-1 whitespace-pre-line text-stone-700" style={style}>{description}</p>;
  }
  return (
    <ul className="mt-1 list-disc space-y-0.5 pl-5 text-stone-700" style={style}>
      {lines.map((line, index) => <li key={index}>{line.replace(/^[-*\s]+/, '')}</li>)}
    </ul>
  );
}

export function AcademicTemplate({ data, settings }: Props) {
  const { personal, experience, education, skills, projects, certifications } = data;
  const s = mergeSettings(settings);
  const hScale = s.sizes.heading / 100;
  const bScale = s.sizes.body / 100;
  const contact = [personal.email, personal.phone, personal.location, personal.website, personal.linkedin].filter(Boolean);
  const headingClass = 'mb-2 border-b border-stone-800 pb-1 font-bold uppercase tracking-widest text-stone-900';

  return (
    <div
      className="bg-stone-50 text-stone-900 h-full"
      style={{
        fontFamily: `${s.fonts.primary}, Georgia, 'Times New Roman', serif`,
        padding: pagePadding(s.margins),
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <header className="text-center" style={{ textAlign: s.headerAlignment }}>
        {s.photo && <img src={s.photo} alt="" className="w-20 h-20 rounded-full object-cover mx-auto mb-3 border border-stone-800" />}
        <h1 className="font-bold tracking-wide" style={{ fontFamily: `${s.fonts.secondary}, Georgia, serif`, fontSize: `${1.875 * hScale}rem` }}>{personal.fullName || 'Your Name'}</h1>
        {contact.length > 0 && <p className="mt-2 text-stone-700" style={{ fontSize: `${0.65 * bScale}rem` }}>{contact.join('  |  ')}</p>}
        <div className="mx-auto mt-4 h-px w-24 bg-stone-800" />
      </header>

      <main className="mt-5" style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.sectionGap }}>
        {personal.summary && (
          <section>
            <h2 className={headingClass} style={{ fontSize: `${0.875 * hScale}rem` }}>Profile</h2>
            <p className="whitespace-pre-line text-stone-700" style={{ fontSize: `${0.75 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{personal.summary}</p>
          </section>
        )}

        {education.length > 0 && (
          <section>
            <h2 className={headingClass} style={{ fontSize: `${0.875 * hScale}rem` }}>Education</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {education.map((item) => (
                <article key={item.id}>
                  <div className="flex items-baseline justify-between gap-4">
                    <h3 className="font-bold" style={{ fontSize: `${0.875 * hScale}rem` }}>{item.school}{item.location && `, ${item.location}`}</h3>
                    <span className="shrink-0 italic" style={{ fontSize: `${0.65 * bScale}rem` }}>
                      {formatDate(item.startDate, s.dateFormat)} - {formatDate(item.endDate, s.dateFormat)}
                    </span>
                  </div>
                  <p className="italic" style={{ fontSize: `${0.75 * bScale}rem` }}>{item.degree}{item.field && ` in ${item.field}`}</p>
                  {item.description && <p className="mt-0.5 whitespace-pre-line text-stone-700" style={{ fontSize: `${0.75 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{item.description}</p>}
                </article>
              ))}
            </div>
          </section>
        )}

        {experience.length > 0 && (
          <section>
            <h2 className={headingClass} style={{ fontSize: `${0.875 * hScale}rem` }}>Professional Experience</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {experience.map((item) => (
                <article key={item.id}>
                  <div className="flex items-baseline justify-between gap-4">
                    <h3 className="font-bold" style={{ fontSize: `${0.875 * hScale}rem` }}>{item.title}</h3>
                    <span className="shrink-0 italic" style={{ fontSize: `${0.65 * bScale}rem` }}>
                      {formatDate(item.startDate, s.dateFormat)} - {item.current ? 'Present' : formatDate(item.endDate, s.dateFormat)}
                    </span>
                  </div>
                  <p className="italic" style={{ fontSize: `${0.75 * bScale}rem` }}>{item.company}{item.location && `, ${item.location}`}</p>
                  {renderDescription(item.description, s.spacing.lineSpacing, bScale)}
                </article>
              ))}
            </div>
          </section>
        )}

        {projects.length > 0 && (
          <section>
            <h2 className={headingClass} style={{ fontSize: `${0.875 * hScale}rem` }}>Selected Projects</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {projects.map((project) => (
                <article key={project.id}>
                  <h3 className="font-bold" style={{ fontSize: `${0.875 * hScale}rem` }}>{project.name}{project.url && <span className="ml-2 font-normal italic" style={{ fontSize: `${0.6 * bScale}rem` }}>{project.url}</span>}</h3>
                  {project.description && <p className="mt-0.5 whitespace-pre-line text-stone-700" style={{ fontSize: `${0.75 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{project.description}</p>}
                  {project.technologies.length > 0 && <p className="italic text-stone-600" style={{ fontSize: `${0.65 * bScale}rem` }}>Methods and tools: {project.technologies.join(', ')}</p>}
                </article>
              ))}
            </div>
          </section>
        )}

        {skills.length > 0 && (
          <section>
            <h2 className={headingClass} style={{ fontSize: `${0.875 * hScale}rem` }}>Areas of Expertise</h2>
            <p style={{ fontSize: `${0.75 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{skills.map((skill) => skill.name).join('; ')}</p>
          </section>
        )}

        {certifications.length > 0 && (
          <section>
            <h2 className={headingClass} style={{ fontSize: `${0.875 * hScale}rem` }}>Certifications</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.titleGap, fontSize: `${0.75 * bScale}rem` }}>
              {certifications.map((certification) => (
                <div key={certification.id} className="flex items-baseline justify-between gap-4">
                  <p><span className="font-bold">{certification.name}</span>, {certification.issuer}{certification.url && <span className="italic"> ({certification.url})</span>}</p>
                  {certification.date && <span className="shrink-0 italic">{certification.date}</span>}
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
